/**
 * Barras de la noche de lanzamiento (brand book SOA 10 PRO · §13):
 *  · AvisoPrevio   — banner NO cerrable desde 30 min antes del mantenimiento, con cuenta regresiva real.
 *  · ModoPruebaBar — barra violeta para los que sí entran mientras el OS está cerrado para el resto,
 *                    con «Ver como» (cambia la barra de navegación por rol, solo en este navegador).
 * Ninguna escribe datos: todo se lee de /api/acceso/estado.
 */
import { useEffect, useState } from "react";
import { useAccesoEstado, VER_COMO_KEY, leerVerComo } from "../../lib/useAccesoEstado.js";
import { useUserPref } from "../../hooks/useUserPref.js";
import { useAuth } from "../../context/AuthContext.jsx";
import BienvenidaSOA10 from "../BienvenidaSOA10.jsx";
import { PaginaSesionVencida, PaginaSinConexion } from "./PaginasSistema.jsx";
import { useAuth as useAuthSesion } from "../../context/AuthContext.jsx";

const pad = (n) => String(n).padStart(2, "0");
const VENTANA_AVISO_MS = 30 * 60 * 1000;

function AvisoPrevio({ estado }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(n => n + 1), 1000); return () => clearInterval(t); }, []);
  const inicio = estado?.inicio ? new Date(estado.inicio).getTime() : null;
  if (!inicio || estado.restringido) return null;
  const falta = inicio - (Date.now() + (estado.desfase || 0));
  if (falta <= 0 || falta > VENTANA_AVISO_MS) return null;
  const hora = new Date(inicio).toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit", hour12: false });
  const mm = Math.floor(falta / 60000), ss = Math.floor((falta % 60000) / 1000);
  return (
    <div role="status" style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9990, background: "#FEF3C7", color: "#92400E", display: "flex", alignItems: "center", justifyContent: "center", gap: 14, padding: "10px 16px", fontSize: 14, fontWeight: 700, flexWrap: "wrap" }}>
      <span>A las {hora} el OS entra en mantenimiento para instalar SOA 10 PRO. Guardá lo que estés escribiendo.</span>
      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", background: "#FDE68A", borderRadius: 8, padding: "2px 8px" }}>{pad(mm)}:{pad(ss)}</span>
    </div>
  );
}

const VISTAS = [["", "Yo"], ["ec", "EC"], ["ccc", "CCC"], ["cfo", "CFO"], ["creativos", "Creativos"]];

function ModoPruebaBar({ estado }) {
  const [vista, setVista] = useState(leerVerComo() || "");
  if (!estado?.restringido || !estado.permitido) return null;
  const cambiar = (v) => {
    setVista(v);
    try { v ? localStorage.setItem(VER_COMO_KEY, v) : localStorage.removeItem(VER_COMO_KEY); } catch { /* sin storage */ }
    window.location.reload();   // la barra de navegación se resuelve al montar el layout
  };
  const nombres = (estado.permitidos || []).map(e => e.split("@")[0]).join(", ");
  return (
    <div role="status" className="modo-prueba" style={{ background: "#5B21B6", color: "#FFFFFF", display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", fontSize: 13, fontWeight: 700, flexWrap: "wrap" }}>
      {/* En celular una sola línea: «Modo prueba» + los chips con scroll lateral (antes eran 3 líneas fijas). */}
      <span className="hidden sm:inline"><span aria-hidden="true" className="msr mr-1 align-[-0.18em] text-[1.15em]">lock</span>Modo prueba — el OS está cerrado para el resto{nombres ? ` · entran ${nombres}` : ""}</span>
      <span className="sm:hidden" title={`El OS está cerrado para el resto${nombres ? ` · entran ${nombres}` : ""}`}><span aria-hidden="true" className="msr mr-1 align-[-0.18em] text-[1.15em]">lock</span>Prueba</span>
      <span className="sm:ml-auto" style={{ display: "inline-flex", alignItems: "center", gap: 6, overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
        <span className="hidden sm:inline" style={{ opacity: 0.8, fontWeight: 600 }}>Ver como:</span>
        {VISTAS.map(([k, l]) => (
          <button key={k || "yo"} type="button" onClick={() => cambiar(k)} aria-pressed={vista === k}
                  style={{ border: 0, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 800, cursor: "pointer", background: vista === k ? "#FFFFFF" : "rgba(255,255,255,0.16)", color: vista === k ? "#5B21B6" : "#FFFFFF" }}>
            {l}
          </button>
        ))}
      </span>
    </div>
  );
}

/** Sesión vencida (401 sin posibilidad de renovar el token) → página de sistema; «Entrar de nuevo» cierra sesión. */
function SesionVencida() {
  const [vencida, setVencida] = useState(false);
  const { signOut } = useAuthSesion();
  useEffect(() => {
    const on = () => setVencida(true);
    window.addEventListener("crew-session-expired", on);
    return () => window.removeEventListener("crew-session-expired", on);
  }, []);
  if (!vencida) return null;
  const entrar = async () => {
    try { sessionStorage.setItem("crew_volver_a", window.location.pathname + window.location.search); } catch { /* sin storage */ }
    try { await signOut(); } catch { /* ya sin sesión */ }
    window.location.assign("/login");
  };
  return <div role="alertdialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 10000 }}><PaginaSesionVencida onEntrar={entrar} /></div>;
}

/** Sin conexión: aviso flotante que no tapa la app (la página completa queda para cuando no carga nada). */
function SinConexion() {
  const [off, setOff] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const on = () => setOff(false), of = () => setOff(true);
    window.addEventListener("online", on); window.addEventListener("offline", of);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", of); };
  }, []);
  if (!off) return null;
  return <div role="status" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 100, zIndex: 9990, background: "#111318", color: "#FFFFFF", borderRadius: 16, padding: "10px 16px", fontSize: 14, fontWeight: 700, boxShadow: "0 4px 16px rgba(17,19,24,0.14)" }}>Sin conexión · mostramos lo último que guardamos</div>;
}

export default function BarrasNoche() {
  const estado = useAccesoEstado();
  return <><SesionVencida /><SinConexion />{estado && <BarrasConEstado estado={estado} />}</>;
}

function BarrasConEstado({ estado }) {
  // Bienvenida (brand book 6, 15e): con el OS abierto se guarda una vez por persona; en modo prueba se muestra una vez por sesion y no se guarda.
  return <><AvisoPrevio estado={estado} /><ModoPruebaBar estado={estado} /><BienvenidaSOA10 guardar={!estado.restringido} /></>;
}
